// AssetDelete.js
// React 19.2.3 + antd 6.1.3
//
// Модальная форма удаления карточки с опциями:
// 1) Удалить визуальные представления
// 2) Проверить использование карточки
//
// Поведение:
// - По "Удалить": если включена проверка, выполняется checkUsagePromise(assetId)
//   -> если есть ссылки, показываем предупреждение и требуем нажать "Всё равно удалить".
// - После подтверждения запускается удаление с прогресс-баром.
// - Удаление состоит из промисов: deleteViewsPromise(assetId) (опционально) и deleteCardPromise(assetId).
// - После успеха кнопка "Удалить" меняется на "Закрыть", а "Отмена" скрывается.
//
// Интеграция:
// Передайте реальные промисы через props:
//   checkUsagePromise(assetId) -> Promise<Array<{ id: string|number, title?: string } | string>>
//   deleteViewsPromise(assetId) -> Promise<void>
//   deleteCardPromise(assetId) -> Promise<void>

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Checkbox, Button, Progress, Alert, Typography, Space, message } from "antd";

const { Text } = Typography;

function normalizeUsageList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => {
      if (typeof x === "string" || typeof x === "number") return { id: x, title: String(x) };
      if (x && (typeof x.id === "string" || typeof x.id === "number")) {
        return { id: x.id, title: x.title ? String(x.title) : String(x.id) };
      }
      return null;
    })
    .filter(Boolean);
}

export default function AssetDelete({
  open,
  onClose,
  assetId,
  assetTitle,

  // Реальные промисы / API:
  checkUsagePromise,
  deleteViewsPromise,
  deleteCardPromise,

  // Опционально: начальные значения
  initialDeleteViews = true,
  initialCheckUsage = true,
}) {
  const [form] = Form.useForm();

  const [phase, setPhase] = useState("idle"); // idle | checking | confirm_force | deleting | done | error
  const [forceDelete, setForceDelete] = useState(false);

  const [usageList, setUsageList] = useState([]);
  const [percent, setPercent] = useState(0);
  const [progressStatus, setProgressStatus] = useState("active"); // active | success | exception
  const [errorText, setErrorText] = useState("");

  const isMountedRef = useRef(false);

  const title = useMemo(() => {
    const base = "Удаление карточки";
    if (assetTitle) return `${base}: ${assetTitle}`;
    if (assetId !== undefined && assetId !== null && assetId !== "") return `${base} #${assetId}`;
    return base;
  }, [assetId, assetTitle]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    // Сброс состояния при открытии
    setPhase("idle");
    setForceDelete(false);
    setUsageList([]);
    setPercent(0);
    setProgressStatus("active");
    setErrorText("");

    form.setFieldsValue({
      deleteViews: !!initialDeleteViews,
      checkUsage: !!initialCheckUsage,
    });
  }, [open, form, initialDeleteViews, initialCheckUsage]);

  const safeSetState = (fn) => {
    if (isMountedRef.current) fn();
  };

  const closeModal = () => {
    // Закрытие разрешаем всегда, но во время удаления блокируем кнопки в UI (см. footer)
    onClose?.();
  };

  const runCheckUsage = async () => {
    if (typeof checkUsagePromise !== "function") {
      // Нет промиса проверки — считаем, что ссылок нет
      return [];
    }
    const res = await checkUsagePromise(assetId);
    return normalizeUsageList(res);
  };

  const runDeletion = async ({ deleteViews }) => {
    const steps = [];
    if (deleteViews) steps.push("views");
    steps.push("card");

    const total = steps.length;
    const stepDonePercent = (done) => Math.round((done / total) * 100);

    // Старт
    safeSetState(() => {
      setPercent(5);
      setProgressStatus("active");
    });

    let done = 0;

    if (deleteViews) {
      if (typeof deleteViewsPromise !== "function") {
        throw new Error("Не передан deleteViewsPromise(assetId).");
      }
      await deleteViewsPromise(assetId);
      done += 1;
      safeSetState(() => setPercent(Math.max(10, stepDonePercent(done))));
    }

    if (typeof deleteCardPromise !== "function") {
      throw new Error("Не передан deleteCardPromise(assetId).");
    }
    await deleteCardPromise(assetId);
    done += 1;
    safeSetState(() => setPercent(Math.max(20, stepDonePercent(done))));

    safeSetState(() => {
      setPercent(100);
      setProgressStatus("success");
    });
  };

  const handlePrimaryClick = async () => {
    // После завершения — "Закрыть"
    if (phase === "done") {
      closeModal();
      return;
    }

    const values = form.getFieldsValue();
    const deleteViews = !!values.deleteViews;
    const checkUsage = !!values.checkUsage;

    // 1) Проверка использования (если включено и ещё не подтверждали force)
    if (checkUsage && !forceDelete) {
      try {
        safeSetState(() => {
          setPhase("checking");
          setErrorText("");
          setUsageList([]);
        });

        const list = await runCheckUsage();

        if (!isMountedRef.current) return;

        if (list.length > 0) {
          safeSetState(() => {
            setUsageList(list);
            setPhase("confirm_force");
          });
          message.warning("Карточка используется. Для удаления нажмите «Всё равно удалить».");
          return;
        }

        // ссылок нет — продолжаем удаление
        safeSetState(() => setPhase("idle"));
      } catch (e) {
        const msg = e?.message || "Ошибка при проверке использования карточки.";
        safeSetState(() => {
          setPhase("error");
          setErrorText(msg);
          setProgressStatus("exception");
        });
        message.error(msg);
        return;
      }
    }

    // 2) Удаление
    try {
      safeSetState(() => {
        setPhase("deleting");
        setErrorText("");
        setPercent(0);
        setProgressStatus("active");
      });

      await runDeletion({ deleteViews });

      if (!isMountedRef.current) return;

      safeSetState(() => setPhase("done"));
      message.success("Удаление выполнено.");
    } catch (e) {
      const msg = e?.message || "Ошибка при удалении.";
      safeSetState(() => {
        setPhase("error");
        setErrorText(msg);
        setProgressStatus("exception");
        setPercent((p) => Math.max(p, 10));
      });
      message.error(msg);
    }
  };

  const primaryText = useMemo(() => {
    if (phase === "done") return "Закрыть";
    if (phase === "confirm_force") return "Всё равно удалить";
    return "Удалить";
  }, [phase]);

  const primaryDanger = useMemo(() => phase !== "done", [phase]);

  const cancelHidden = useMemo(() => phase === "done", [phase]);
  const buttonsDisabled = useMemo(() => phase === "checking" || phase === "deleting", [phase]);

  // При попадании в confirm_force фиксируем forceDelete=true
  useEffect(() => {
    if (phase === "confirm_force") setForceDelete(true);
  }, [phase]);

  return (
    <Modal
      open={open}
      title={title}
      onCancel={buttonsDisabled ? undefined : closeModal}
      destroyOnHidden
      maskClosable={!buttonsDisabled}
      keyboard={!buttonsDisabled}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }} orientation="horizontal">
          <div />
          <Space orientation="horizontal">
            {!cancelHidden && (
              <Button onClick={closeModal} disabled={buttonsDisabled}>
                Отмена
              </Button>
            )}
            <Button
              type="primary"
              danger={primaryDanger}
              onClick={handlePrimaryClick}
              loading={phase === "checking" || phase === "deleting"}
            >
              {primaryText}
            </Button>
          </Space>
        </Space>
      }
    >
      <Form form={form} layout="vertical" disabled={phase === "deleting" || phase === "done"}>
        <Form.Item name="deleteViews" valuePropName="checked">
          <Checkbox>Удалить визуальные представления</Checkbox>
        </Form.Item>

        <Form.Item name="checkUsage" valuePropName="checked">
          <Checkbox>Проверить использование карточки</Checkbox>
        </Form.Item>
      </Form>

      {phase === "confirm_force" && usageList.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Карточка используется"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                Найдены карточки, содержащие ссылку на удаляемую. Удаление может нарушить целостность данных.
              </div>
              <div style={{ maxHeight: 160, overflow: "auto" }}>
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {usageList.map((u) => (
                    <li key={String(u.id)}>
                      <Text>{u.title}</Text>
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ marginTop: 8 }}>
                Нажмите <Text strong>«Всё равно удалить»</Text>, чтобы продолжить.
              </div>
            </div>
          }
        />
      )}

      {(phase === "deleting" || phase === "done" || phase === "error") && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={percent} status={progressStatus} />
          {phase === "deleting" && <Text type="secondary">Выполняется удаление…</Text>}
          {phase === "done" && <Text type="success">Удаление завершено.</Text>}
          {phase === "error" && !!errorText && (
            <Alert style={{ marginTop: 12 }} type="error" showIcon message="Ошибка" description={errorText} />
          )}
        </div>
      )}
    </Modal>
  );
}
